// Helper-Funktionen für korrekte Zeitzone (MESZ/MEZ)

/**
 * Gibt das aktuelle Datum/Zeit in deutscher Zeitzone zurück
 * @param format - 'iso' für ISO-String, 'date' für Date-only, 'datetime' für volles Format
 */
export function getGermanTime(format: 'iso' | 'date' | 'datetime' = 'iso'): string {
  const now = new Date();
  
  // Optionen für deutsche Zeitzone
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  };
  
  if (format === 'date') {
    // Nur Datum (YYYY-MM-DD)
    options.hour = undefined;
    options.minute = undefined;
    options.second = undefined;
    const parts = new Intl.DateTimeFormat('en-CA', options).format(now);
    return parts; // en-CA gibt YYYY-MM-DD Format
  }
  
  if (format === 'datetime') {
    // Deutsches Format (DD.MM.YYYY HH:MM:SS)
    return new Intl.DateTimeFormat('de-DE', options).format(now);
  }
  
  // ISO-ähnliches Format aber mit korrekter Zeitzone
  // Wir müssen es manuell bauen, da toISOString() immer UTC verwendet
  const germanDate = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  
  // Offset berechnen (MESZ = +2, MEZ = +1)
  const offset = -germanDate.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offset) / 60);
  const offsetMinutes = Math.abs(offset) % 60;
  const offsetSign = offset >= 0 ? '+' : '-';
  const offsetString = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
  
  // ISO-String mit korrektem Offset bauen
  const year = germanDate.getFullYear();
  const month = (germanDate.getMonth() + 1).toString().padStart(2, '0');
  const day = germanDate.getDate().toString().padStart(2, '0');
  const hours = germanDate.getHours().toString().padStart(2, '0');
  const minutes = germanDate.getMinutes().toString().padStart(2, '0');
  const seconds = germanDate.getSeconds().toString().padStart(2, '0');
  const milliseconds = germanDate.getMilliseconds().toString().padStart(3, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetString}`;
}

/**
 * Konvertiert einen UTC ISO-String in deutsche Zeit
 */
export function utcToGermanTime(utcString: string, format: 'iso' | 'date' | 'datetime' = 'datetime'): string {
  const date = new Date(utcString);
  
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  };
  
  if (format === 'date') {
    options.hour = undefined;
    options.minute = undefined;
    options.second = undefined;
    return new Intl.DateTimeFormat('en-CA', options).format(date);
  }
  
  if (format === 'datetime') {
    return new Intl.DateTimeFormat('de-DE', options).format(date);
  }
  
  // ISO Format mit Zeitzone
  const germanDate = new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  const year = germanDate.getFullYear();
  const month = (germanDate.getMonth() + 1).toString().padStart(2, '0');
  const day = germanDate.getDate().toString().padStart(2, '0');
  const hours = germanDate.getHours().toString().padStart(2, '0');
  const minutes = germanDate.getMinutes().toString().padStart(2, '0');
  const seconds = germanDate.getSeconds().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Formatiert ein Datum für die Anzeige
 */
export function formatGermanDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(d);
}
